import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { LockSimple, Backspace } from "@phosphor-icons/react";
import { Mascot } from "@/components/styleguide/Mascot";
import { BloomButton } from "@/components/styleguide/ui/BloomButton";
import { api } from "./api";
import { useAuth } from "./AuthContext";

export const getMode = () => localStorage.getItem("bloom-vocab-mode") || "parent";
export const setMode = (m) => localStorage.setItem("bloom-vocab-mode", m);

const DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "", "0", "back"];

export function ParentGate({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [locked, setLocked] = useState(() => getMode().startsWith("child"));
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (locked && user && !user.has_pin) {
      setMode("parent");
      setLocked(false);
    }
  }, [locked, user]);

  useEffect(() => {
    if (pin.length !== 4 || checking) return;
    (async () => {
      setChecking(true);
      try {
        const res = await api("/auth/pin/verify", { method: "POST", body: { pin } });
        if (res.valid) {
          setMode("parent");
          setLocked(false);
        } else {
          setError("That's not the right PIN. Try again!");
          setPin("");
        }
      } catch (e) {
        setError(e.message);
        setPin("");
      } finally {
        setChecking(false);
      }
    })();
  }, [pin, checking]);

  const press = (d) => {
    setError("");
    if (d === "back") setPin((p) => p.slice(0, -1));
    else if (d && pin.length < 4) setPin((p) => p + d);
  };

  if (!locked) return children;

  return (
    <div className="flex min-h-[100svh] items-center justify-center bg-gradient-to-b from-[#FFF7EF] to-[#DCF3EC] px-5" data-testid="parent-pin-gate">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm rounded-[2rem] bg-card p-7 text-center shadow-soft-lg">
        <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-accent">
          <LockSimple size={26} weight="bold" className="text-primary-strong" />
        </span>
        <h1 className="mt-3 font-display text-2xl font-black text-foreground">Grown-ups only</h1>
        <p className="mt-1 text-sm font-semibold text-muted-foreground">Enter your Parent PIN to continue.</p>

        <div className="mt-5 flex justify-center gap-3" data-testid="pin-dots">
          {[0, 1, 2, 3].map((i) => (
            <span key={i} className={`h-4 w-4 rounded-full transition-colors ${i < pin.length ? "bg-primary" : "bg-muted"}`} />
          ))}
        </div>
        <AnimatePresence>
          {error && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="mt-2 text-sm font-bold text-destructive" data-testid="pin-error">
              {error}
            </motion.p>
          )}
        </AnimatePresence>

        <div className="mx-auto mt-5 grid max-w-[240px] grid-cols-3 gap-2.5">
          {DIGITS.map((d, i) =>
            d === "" ? (
              <span key={i} />
            ) : (
              <button
                key={i}
                data-testid={d === "back" ? "pin-back" : `pin-${d}`}
                onClick={() => press(d)}
                className="flex min-h-[56px] items-center justify-center rounded-2xl bg-muted font-display text-xl font-black text-foreground transition hover:bg-border active:scale-95"
              >
                {d === "back" ? <Backspace size={22} weight="bold" /> : d}
              </button>
            ),
          )}
        </div>

        <BloomButton variant="ghost" size="sm" className="mt-5" onClick={() => navigate(-1)} data-testid="pin-cancel">
          Go back
        </BloomButton>
        <div className="mt-1 flex justify-center opacity-70">
          <Mascot size={40} animated={false} mood="encouraging" />
        </div>
      </motion.div>
    </div>
  );
}
